// public/v3/src/lib/careplan-bhi.js — program-aware BHI prompt extension
// for the unified care-plan generator.
//
// Care-plan generation is unified per (patient, month) regardless of
// program. When a patient is BHI-enrolled, an additional <bhi_section>
// block is appended to the assembled CCM prompt so the AI produces a
// Behavioral Health Care Planning section alongside the chronic-condition
// sections (or alone, in the rare BHI-only case).
//
// Lives separately from careplan-generate.js so Jest can require this
// helper directly (careplan-generate.js is ESM with browser imports and
// can't be Jest-loaded). careplan-generate.js imports from this module
// via ESM interop, same pattern as bhi-billing.js.
//
// Module format: CommonJS (module.exports). Pure function only — no React,
// no fetches.
//
// Known limitation (deferred): a BHI-only patient (no CCM enrollment) still
// flows through the CCM-focused prompt template; the appended <bhi_section>
// instructs the AI on the behavioral side but the CCM scaffolding will
// produce partly-irrelevant chronic-condition headers. A future phase may
// route BHI-only patients through a dedicated BHI-only template.

const { _latestAssessmentForMonth, entryProgramFamily } = require('./bhi-billing');
// Chart free text is tag-neutralized at render time so a stored note can
// never close </bhi_section> or open a fake tag (see prompt-text.js; same
// defect class as the pain-section fix, adversarial review 2026-07-21).
const { promptSafeLine } = require('./prompt-text');

function entryActivityType(entry) {
  const type = promptSafeLine((entry && (entry.type || entry.label || entry.desc)) || '', 80);
  return type || 'BHI activity';
}

function entryActivityNote(entry) {
  const e = entry || {};
  const note = promptSafeLine(e.notes || e.description || e.desc || e.hint || '', 200);
  const type = entryActivityType(e);
  const label = promptSafeLine(e.label || '', 80);
  if (!note || note === type || note === label) return '';
  return note;
}

function buildBhiActivityLines(patient, monthStr) {
  const log = Array.isArray(patient && patient.activityLog) ? patient.activityLog : [];
  const month = String(monthStr || '');
  const entries = log
    .filter((e) => e && !e.isConsolidatedNote && (!month || String(e.date || '').startsWith(month)))
    .filter((e) => entryProgramFamily(e) === 'bhi')
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  if (!entries.length) return '(no BHI activity-log entries supplied for this month)';
  return entries.slice(0, 8).map((e) => {
    const date = e.date || 'undated';
    const duration = parseInt(e.duration, 10) || 0;
    const note = entryActivityNote(e);
    return `- ${date}: ${duration} min ${entryActivityType(e)}${note ? ` — ${note}` : ''}`;
  }).join('\n');
}

function buildBhiPromptSection(patient, monthStr) {
  if (!patient || !patient.billing || !patient.billing.bhiProgram) return '';
  const assessments = (patient.bhi && patient.bhi.assessments) || [];
  // Latest BY DATE through the service month — matches the deterministic
  // RATING SCALE block and avoids citing future-month assessments.
  const latest = _latestAssessmentForMonth(assessments, monthStr);
  const isCcm = !!patient.billing.program;
  const dx = promptSafeLine((patient.bhi && patient.bhi.dx) || '', 160);
  // Scale ids can be practice-authored (settings custom scales) — same
  // free-text treatment as the activity lines.
  const assessmentLine = latest
    ? `${promptSafeLine(latest.scale, 60)} score ${promptSafeLine(latest.score, 12)} on ${promptSafeLine(latest.date, 20)}`
    : '(no assessment yet)';
  const activityLines = buildBhiActivityLines(patient, monthStr);
  const crossRef = isCcm
    ? 'Note that this is a comprehensive care plan covering both chronic care management AND behavioral health integration; reference both contexts.'
    : 'This care plan focuses on behavioral health integration.';
  return `

<bhi_section>
The patient is enrolled in Behavioral Health Integration (CPT 99484) for ${dx || 'a behavioral health condition'}. Include a Behavioral Health Care Planning section in the care plan covering:
- Behavioral health condition managed: ${dx}
- Most recent assessment: ${assessmentLine}
- Documented BHI activity this month:
${activityLines}
- Use only the behavioral health diagnosis, most recent assessment, documented BHI activity lines above, and chart data already supplied in the main prompt. Do NOT state that behavioral interventions, treatment-plan review, patient outreach, medication changes, referrals, safety planning, or outside behavioral health coordination occurred unless those actions are explicitly documented in those sources.
- If only diagnosis and assessment data are supplied, describe the assessment-informed monitoring needs only; do NOT add a "none documented" statement about behavioral interventions or outside behavioral health coordination.
${crossRef}
</bhi_section>
`;
}

module.exports = {
  buildBhiPromptSection,
  buildBhiActivityLines,
};
